/**
 * B3 voxel dissolve — patches an existing `MeshStandardMaterial` with
 * a noise-threshold `uProgress` discard + emissive edge band.
 *
 * Why patch instead of new ShaderMaterial: the chassis lighting,
 * shadow, AO, and PBR write paths are already correct on
 * MeshStandardMaterial. `onBeforeCompile` keeps all of that and
 * inserts a discard + edge-glow pass without re-implementing the
 * lighting math.
 *
 * Per-voxel hash drives the discard threshold (each instance gets a
 * stable random in [0,1]; we discard when uProgress > hash, so cubes
 * vanish in a noise-stable order across frames). An emissive band
 * just above the discard frontier glows in the dissolve color.
 *
 * Three uniforms exposed on `material.userData.dissolveUniforms`:
 *   - uProgress  0..1, 0 = intact chassis, 1 = fully dissolved
 *   - uEdgeColor vec3 RGB; emissive band color (typically white)
 *   - uEdgeWidth float; band width as a fraction of uProgress space
 *
 * Pure setup function; no Three runtime per frame. Caller mutates
 * uniforms in their own useFrame.
 */

import { Color, type Material, type MeshStandardMaterial } from "three";

export interface DissolveUniforms {
  uProgress: { value: number };
  uEdgeColor: { value: Color };
  uEdgeWidth: { value: number };
}

const VERTEX_HEAD = /* glsl */ `
varying float vDissolveHash;
`;

const VERTEX_BODY = /* glsl */ `
// Pseudo-random hash from a per-voxel stable position. Reads the
// instanceMatrix translation column when the material is bound to an
// InstancedMesh (USE_INSTANCING is defined by Three.js's chunk system);
// falls back to the per-vertex model-space position otherwise so the
// shader still compiles on plain Mesh draws.
{
  #ifdef USE_INSTANCING
    vec3 ipos = vec3(instanceMatrix[3][0], instanceMatrix[3][1], instanceMatrix[3][2]);
  #else
    vec3 ipos = (modelMatrix * vec4(position, 1.0)).xyz;
  #endif
  vDissolveHash = fract(sin(dot(ipos, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
}
`;

const FRAGMENT_HEAD = /* glsl */ `
uniform float uProgress;
uniform vec3 uEdgeColor;
uniform float uEdgeWidth;
varying float vDissolveHash;
`;

// Inject right at the top of the fragment main body so the discard
// short-circuits before any lighting math runs.
const FRAGMENT_BODY = /* glsl */ `
{
  // Discard voxels whose hash falls below the dissolve frontier.
  if (vDissolveHash < uProgress) discard;
  // Emissive band just above the frontier — fades over uEdgeWidth.
  float edge = vDissolveHash - uProgress;
  if (edge < uEdgeWidth && uProgress > 0.001) {
    float k = 1.0 - edge / max(0.0001, uEdgeWidth);
    totalEmissiveRadiance += uEdgeColor * k * 4.0;
  }
}
`;

/**
 * Patch the material in-place. Idempotent — calling twice has no
 * extra effect because the shader source markers we replace become
 * unique once injected.
 */
export function applyDissolvePatch(
  material: MeshStandardMaterial,
  initial: { progress?: number; edgeColor?: string; edgeWidth?: number } = {},
): DissolveUniforms {
  const uniforms: DissolveUniforms = {
    uProgress: { value: initial.progress ?? 0 },
    uEdgeColor: { value: new Color(initial.edgeColor ?? "#ffffff") },
    uEdgeWidth: { value: initial.edgeWidth ?? 0.06 },
  };
  // Store on userData so consumers can find them via the mesh ref.
  (material.userData as Record<string, unknown>).dissolveUniforms = uniforms;

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uProgress = uniforms.uProgress;
    shader.uniforms.uEdgeColor = uniforms.uEdgeColor;
    shader.uniforms.uEdgeWidth = uniforms.uEdgeWidth;

    // Vertex: insert varying + hash computation.
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\n${VERTEX_HEAD}`)
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>\n${VERTEX_BODY}`,
      );

    // Fragment: insert uniforms + varying + discard/edge body.
    // We inject the body after the emissive map block so
    // `totalEmissiveRadiance` exists.
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\n${FRAGMENT_HEAD}`)
      .replace(
        "#include <emissivemap_fragment>",
        `#include <emissivemap_fragment>\n${FRAGMENT_BODY}`,
      );
  };
  // Force recompile if the material was already compiled before patch.
  material.needsUpdate = true;
  return uniforms;
}

/**
 * Lookup the uniforms stored on a previously-patched material.
 * Returns `null` for materials that were never patched.
 */
export function getDissolveUniforms(
  material: Material,
): DissolveUniforms | null {
  const u = (material.userData as Record<string, unknown>)?.dissolveUniforms;
  return (u as DissolveUniforms | undefined) ?? null;
}
